import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Spinner, OverlayTrigger, Tooltip } from "react-bootstrap";
import TabellaLista from '../../../../components/Tabella/TabellaLista';
import { ChipFilter } from '../../../../components/Tabella/ChipFilter';
import MyApiClient from '../../../../../api-client/MyApiClient';
import DEFAULT_USER from "../../../../../images/DEFAULT_USER.png";
import { dateTimeSort } from '../../../../components/Util/oders';
import { useNavigate } from 'react-router-dom';

import { formattaPrezzo } from "../../../../components/Util/prezzi";
/**
 * Lo stato di un documento e` un insieme chiuso di quattro valori: chiederlo a
 * testo libero non aiutava nessuno, e soprattutto non funzionava - la colonna
 * contiene l'oggetto {label, class}, quindi il filtro testuale confrontava
 * quello che si scriveva con "[object Object]" e non trovava mai niente.
 *
 * Il confronto avviene sul codice grezzo tenuto da parte nella riga, non
 * sull'etichetta mostrata, cosi` rinominare un'etichetta non rompe il filtro.
 */
/**
 * Anche il tipo di documento e` un insieme chiuso: o fattura o ricevuta.
 *
 * Il filtro guarda il codice grezzo (`INVOICE`/`RECEIPT`) e non l'etichetta
 * mostrata, come per lo stato: sono le etichette a poter cambiare, non i codici.
 */
export const filtraPerTipoDocumento = (rows, columnIds, filterValue) =>
    filterValue ? rows.filter(row => row.original.type === filterValue) : rows;

export const filtraPerStato = (rows, columnIds, filterValue) =>
    filterValue ? rows.filter(row => row.original.status === filterValue) : rows;

// Valori dei filtri a chip: il valore e` il codice grezzo della riga, l'etichetta quella in lista.
const TIPO_DOCUMENTO_OPTIONS = [
    { value: 'INVOICE', label: 'Fattura' },
    { value: 'RECEIPT', label: 'Ricevuta' },
];
const STATO_OPTIONS = [
    { value: 'NOT_GENERATED', label: 'Non generato' },
    { value: 'NOT_SENT', label: 'Non inviato' },
    { value: 'SENT', label: 'Inviato' },
    { value: 'ACCEPTED', label: 'Accettato' },
    { value: 'REJECTED', label: 'Rifiutato' },
];

// Chip a scelta multipla: un codice fuori dai chip (es. stato vuoto) resta sempre visibile.
const passaFiltro = (options, accesi, codice) =>
    !options.some((o) => o.value === codice) || accesi.includes(codice);

export const UserTransactionsTable = () => {

	const navigate = useNavigate();

	const [data_json, setDataJson] = useState(null);
	// Chip a scelta multipla: all'inizio tutti accesi
	const [tipoDocumentoFilter, setTipoDocumentoFilter] = useState(TIPO_DOCUMENTO_OPTIONS.map((o) => o.value));
	const [statoFilter, setStatoFilter] = useState(STATO_OPTIONS.map((o) => o.value));

	const filteredData = useMemo(
		() =>
			data_json &&
			data_json.filter(
				(row) =>
					passaFiltro(TIPO_DOCUMENTO_OPTIONS, tipoDocumentoFilter, row.type) &&
					passaFiltro(STATO_OPTIONS, statoFilter, row.status),
			),
		[data_json, tipoDocumentoFilter, statoFilter],
	);

	const [loadingShowed, setLoadingShowed] = useState(false);

	const data_column = [
		{
			Header: 'Data',
			Footer: 'Data',
			accessor: 'invoice_date',
			disableGlobalFilter: true,
			sortType: dateTimeSort,
		},
		{
			Header: 'Tipo',
			Footer: 'Tipo',
			accessor: 'type_document',
			disableGlobalFilter: true,
			filter: filtraPerTipoDocumento
		},
		{
			Header: 'Numero',
			Footer: 'Numero',
			accessor: 'invoice_number',
		},
		{
			Header: 'Importo',
			Footer: 'Importo',
			accessor: 'price',
			disableGlobalFilter: true,
		},
		{
			Header: 'Nome e cognome',
			accessor: 'playerName',
			Cell: ({ row }) => {
				return (
					<div style={{ display: 'flex', alignItems: 'center' }}>
						<img
							src={row.original.imageUrl ? row.original.imageUrl : DEFAULT_USER}
							alt={row.original.playerName}
							style={{
								width: '40px',
								height: '40px',
								borderRadius: '50%',
								marginRight: '10px'
							}}
						/>
						{/* Il nome porta alla scheda del player; la riga, come prima, al dettaglio
						    della transazione. */}
						{row.original.playerId ? (
							<span
								className="sid-cell-link"
								role="button"
								tabIndex={0}
								title="Apri la scheda del cliente"
								onClick={(e) => { e.stopPropagation(); navigate('/struttura/clienti/player_profile/' + row.original.playerId); }}
								onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); navigate('/struttura/clienti/player_profile/' + row.original.playerId); } }}
							>
								{row.original.playerName}
							</span>
						) : (
							<div>{row.original.playerName}</div>
						)}
					</div>
				)
			}
		},
		{
			Header: 'Stato',
			Footer: 'Stato',
			accessor: 'status_label',
			disableGlobalFilter: true,
			filter: filtraPerStato,
			Cell: ({ value, row }) => {
				const errors = row.original.sdi_rejection_errors;
				const hasErrors = errors && errors.length > 0;

				return (
					<div className="d-flex align-items-center">
						<i className={`fa fa-circle me-1 ${value?value.class:''}`}></i>
						{value?value.label:''}
						{hasErrors && (
							<OverlayTrigger
								placement="top"
								overlay={
									<Tooltip id={`tooltip-sdi-${row.original.id}`}>
										<div className="text-start">
											{errors.map((err, idx) => (
												<div key={idx}><strong>{err.Codice}</strong>: {err.Descrizione}</div>
											))}
										</div>
									</Tooltip>
								}
							>
								<i className="fa fa-exclamation-triangle text-danger ms-2" style={{ cursor: 'pointer' }}></i>
							</OverlayTrigger>
						)}
					</div>
				)
			},
		},
	];

	function openDetail(row) {
		navigate('/struttura/booking/userTransactions/'+row.id);
	}
	
	useEffect(() => {
		const fetchData = async () => {
			try {
				const tokenDetailsString = sessionStorage.getItem('userDetails');
				if (tokenDetailsString) {
					const tokenDetails = JSON.parse(tokenDetailsString);
					let token = tokenDetails.idToken;
					const apiClient = new MyApiClient();

					setLoadingShowed(true);
					const [bookingTransactions] = await Promise.all([
						apiClient.getUserBookingTransactions(token)
					]);
					setLoadingShowed(false);

					if(bookingTransactions) {
						let data = [];
						bookingTransactions.filter(element => element.invoice_status !== 'NOT_GENERATED').forEach(element => {

							const options = { year: 'numeric', month: '2-digit', day: '2-digit' };

							const invoiceDateEl = element.invoice_date;
							const invoiceDate = new Date(invoiceDateEl);
							const formattedInvoiceDate = invoiceDate.toLocaleDateString('it-IT', options);

							const createdAt = element.createdAt;
							const dateCreated = new Date(createdAt);
							const options2 = { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' };
							const formattedDate = dateCreated.toLocaleDateString('it-IT', options2);

							let image = null;
                            if(element.playerPicture && element.playerPicture.uri) {
								image = element.playerPicture.uri;
							}

							let status = null;
							if(element.invoice_status === 'SENT') {
								status = {
									"label": "Inviato",
									"class": "text-warning"
								};
							} else if(element.invoice_status === 'ACCEPTED') {
								status = {
									"label": "Accettato",
									"class": "text-success"
								};
							} else if(element.invoice_status === 'REJECTED') {
								status = {
									"label": "Rifiutato",
									"class": "text-danger"
								};
							} else if(element.invoice_status === 'NOT_SENT') {
								status = {
									"label": "Non inviato",
									"class": "text-dark"
								};
							} else if(element.invoice_status === 'NOT_GENERATED') {
								status = {
									"label": "Non generato",
									"class": "text-error"
								};
							}

							let type_document = null;
							if(element.type === 'INVOICE') {
								type_document = 'Fattura';
							} else if(element.type === 'RECEIPT') {
								type_document = 'Ricevuta';
							}

							let row = {
								"id": element.id,
								"playerName": element.playerName,
								"playerId": element.playerId,
								"invoice_date": formattedInvoiceDate,
								"price": formattaPrezzo(element.amount),
								"status": element.invoice_status,
								"status_label": status,
								"type_document": type_document,
								"type": element.type,
								"imageUrl": image,
								"createdAt": formattedDate,
								"invoice_number": element.invoice_number_str,
								"pdf_document": element.pdf_document,
								"sdi_notification": element.sdi_notification,
								"sdi_rejection_errors": element.sdi_rejection_errors
							};
							data.push(row);
							
						});
						setDataJson(data);
					}
				}
			} catch (error) {
				console.error("Error fetching data:", error);
			}
		};

		fetchData();

	}, []);

	return (
		<>
			<Modal className="fade" show={loadingShowed} centered>
				<Modal.Body className="text-center">
				<Spinner animation="border" role="status">
					<span className="sr-only">Loading...</span>
				</Spinner>
				<div>Caricamento in corso...</div>
				</Modal.Body>
			</Modal>

			<TabellaLista
				pageList={true}
				cardTitle={'Fatture e ricevute'}
				hasSearch={true}
				searchPlaceholder={'Cerca per nome o numero documento'}
				hasFilter={false}
				filterControl={
					<div className="sid-chip-filters">
						<ChipFilter label="Tipo documento" options={TIPO_DOCUMENTO_OPTIONS} value={tipoDocumentoFilter} onChange={setTipoDocumentoFilter} multiple />
						<ChipFilter label="Stato" options={STATO_OPTIONS} value={statoFilter} onChange={setStatoFilter} multiple />
					</div>
				}
				data_column={data_column}
				data_json={filteredData}
				openColumn={'playerName'}
				openRow={(row) => openDetail(row)}
				modalCallBack={() => setModalCentered(true)}
				sort_by={'invoice_date'}
                sort_type={'desc'}
			/>
		</>
	)
}
export default UserTransactionsTable;